/* eslint-disable max-lines */
import { MapIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { withServerReason } from "../../../utils/apiErrorMessage";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useSearch } from "../../../hooks/useSearch";
import { IJourneyReservationPassengerInfo } from "../../../interfaces/endpoints/trips/TripChangeNotification";
import { CustomTripWorkingDayType, ITripType } from "../../../interfaces/endpoints/trips/Trips.types";
import { calendarDaysAfter, withDayMarker } from "../../../utils/tripDuration";
import {
  deleteTrip,
  getAllTrips,
  getTripDeletePreview,
  notifyTripDeleteEmails
} from "../../../services/Trips.service";
import AuditAuthor from "../../audit-author/AuditAuthor";
import Table, { Row } from "../../ui/table";
import Pagination from "../../ui/table/Pagination";
import Can from "../../users/permissions/Can";
import useSuperAdmin from "../../users/permissions/useSuperAdmin";
import AffectedReservationsModal from "./AffectedReservationsModal";
import Days from "./Days";
import DeleteTrip from "./DeleteTripModal";
import Prices from "./Prices";

interface LocationState {
  params?: {
    id: number;
  };
  crumbs?: string[];
}

const columns: (string | number)[] = [
  "ID e orarit",
  "Emri i linjës",
  "Nisja",
  "Arritja",
  "Ditët e udhëtimit",
  "Çmimet",
  "Detajet",
  "Veprimet"
];

const renderOperationalInterval = (row: ITripType) => {
  if (row.customWorkingDays && row.customWorkingDays.length > 0) {
    const exceptionDates = row.customWorkingDays.filter(
      (d) => d.type === CustomTripWorkingDayType.ExceptionDate
    );
    const workingDates = row.customWorkingDays.filter(
      (d) => d.type === CustomTripWorkingDayType.SelectedWorkingDate
    );

    return (
      <div className="space-y-2">
        <span className="text-sm text-gray-600 italic block mb-1">
          Ka data specifike të punës
        </span>
        <div className="flex flex-wrap gap-1.5">
          {exceptionDates.length > 0 && (
            <div className="flex items-center gap-1">
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs
                  font-medium bg-[#fef7f5] text-[#f05a2d]"
              >
                Përjashtimet: {format(new Date(exceptionDates[0].date), "dd/MM/yyyy")}
                {exceptionDates.length > 1 && (
                  <span className="ml-1 text-gray-500">
                    +{exceptionDates.length - 1}
                  </span>
                )}
              </span>
            </div>
          )}
          {workingDates.length > 0 && (
            <div className="flex items-center gap-1">
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs
                  font-medium bg-[#fef7f5] text-[#f05a2d]"
              >
                Datat e punës: {format(new Date(workingDates[0].date), "dd/MM/yyyy")}
                {workingDates.length > 1 && (
                  <span className="ml-1 text-gray-500">
                    +{workingDates.length - 1}
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <Days
      days={[
        row.operationalInterval.monday,
        row.operationalInterval.tuesday,
        row.operationalInterval.wednesday,
        row.operationalInterval.thursday,
        row.operationalInterval.friday,
        row.operationalInterval.saturday,
        row.operationalInterval.sunday
      ]}
    />
  );
};

// The arrival carries the day it lands on: "02:00 (+1 ditë)" for an overnight trip, "(+2 ditë)"
// when 28 hours from 22:00 land two calendar days later.
const arrivalWithDayMarker = (trip: ITripType["trip"]): string =>
  withDayMarker(
    trip.arrivalTimeString,
    calendarDaysAfter(trip.departureTimeString, trip.durationMinutes)
  );

const Trips = () => {
  const isSuperAdmin = useSuperAdmin();
  const [data, setData] = useState<ITripType[] | undefined>([]);
  const [showAffectedReservationsModal, setShowAffectedReservationsModal] = useState(false);
  const [affectedReservations, setAffectedReservations] = useState<IJourneyReservationPassengerInfo[]>([]);
  const [pendingDeleteTripId, setPendingDeleteTripId] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useLocation() as { state: LocationState };
  const { response, loading } = useSearch(getAllTrips, {
    withQuerySearch: true
  });
  useEffect(() => {
    setData(response?.data?.result);
  }, [response]);

  const handlePageChange = ({ selected }: { selected: number }) => {
    if (selected !== 0) {
      searchParams.set("pageindex", `${selected + 1}`);
    } else searchParams.delete("pageindex");
    setSearchParams(searchParams, { replace: false });
  };
  const handleDelete = async (id: number) => {
    try {
      // First delete the trip
      await deleteTrip(id);
      toast.success("Orari është fshirë me sukses.", { position: "top-right" });
      setData((prev) => prev?.filter((el) => el.trip.id !== id));

      // For non-super admins, skip preview/notifications
      if (!isSuperAdmin) return;

      // Then check for affected reservations
      const preview = await getTripDeletePreview(id);
      const passengers = preview.data?.affectedPassengers || [];
      if (passengers.length > 0) {
        setAffectedReservations(passengers);
        setPendingDeleteTripId(id);
        setShowAffectedReservationsModal(true);
      }
    } catch (error) {
      toast.error(withServerReason("Ndodhi një gabim gjatë fshirjes.", error), { duration: 6000 });
    }
  };

  const handleTripDeleteNotifyConfirm = async (selectedReservationIds: string[]) => {
    if (!pendingDeleteTripId) return;
    try {
      await notifyTripDeleteEmails(pendingDeleteTripId, selectedReservationIds);
      toast.success("Emailat e njoftimit u dërguan me sukses.", { position: "top-right" });
    } catch {
      toast.error("Ndodhi një gabim gjatë dërgimit të emailave.", { position: "top-right" });
    }
    setShowAffectedReservationsModal(false);
    setPendingDeleteTripId(null);
  };

  const handleTripDeleteContinue =  () => {
    // Trip is already deleted, just close the modal
    setShowAffectedReservationsModal(false);
    setPendingDeleteTripId(null);
  };

  return (
      <div className="flex flex-col" style={{ height: "calc(100vh - 11rem)" }}>
      <Table
        style="hover"
        titles={columns}
        loading={loading}
        className="md:table"
      >
        {data?.length ? (
          data?.map((row) => {
            const highlighted = state?.params?.id === row.trip.id;
            return (
            <Row
              key={row.trip.id}
              id={row.trip.id}
              routeName={row.routeName}
              className={`${highlighted && "highlighted"}`}
              departureTimeString={row.trip.departureTimeString}
              arrivalTimeString={arrivalWithDayMarker(row.trip)}
              operationalInterval={renderOperationalInterval(row)}
              prices={<Prices prices={row.prices} />}
              auditAuthor={
                <AuditAuthor
                  entityName={row.auditAuthor.entityName}
                  entityId={row.auditAuthor.entityId}
                  createdByName={row.auditAuthor.createdByName}
                  createdById={row.auditAuthor.createdById}
                  createdByLastName={row.auditAuthor.createdByLastName}
                  updatedByName={row.auditAuthor.updatedByName}
                  updatedById={row.auditAuthor.updatedById}
                  updatedByLastName={row.auditAuthor.updatedByLastName}
                  insertDate={row.auditAuthor.insertDate}
                  updateDate={row.auditAuthor.updateDate}
                />
              }
              actions={
                <span className="flex items-center">
                  <Can permissions="route.trip.update">
                    <Link
                      className="p-2.5 md:p-1"
                      to={`/trips/edit/${row.trip.id}`}
                      state={{ crumbs: [
                        "Informatat për orarin",
                        "Oraret",
                        "Modifiko Orarin"
                      ],
                      search: searchParams.toString() }}
                    >
                      <PencilSquareIcon className="w-5 h-5 md:w-4" />
                    </Link>
                  </Can>
                  <Can permissions="gtfs.read">
                    <Link
                      className={`p-2.5 md:p-1 ${row.hasGTFS && "text-primary"}
                      hover:text-primary/80 transition-colors`}
                      to={`/gtfs/lines/manage?lineId=${row.trip.lineId}`}
                      state={{
                        crumbs: ["Menaxhimi i GTFS", "Linjat", "Menaxho"]
                      }}
                    >
                      <MapIcon className="w-5 h-5 md:w-4" />
                    </Link>
                  </Can>
                  <Can permissions="route.trip.delete">
                    <DeleteTrip
                      index={row.trip.id}
                      handleDelete={() => handleDelete(row.trip.id)}
                    />
                  </Can>
                </span>
              }
            />
          );
})
        ) : (
          <></>
        )}
      </Table>
      <Pagination
        onPageChange={handlePageChange}
        totalCount={(response?.data.totalPages || 0) * 10}
        length={data?.length}
        page={parseInt(`${searchParams.get("pageindex")}`) || 1}
        pageSize={10}
      />
      {isSuperAdmin && (
        <AffectedReservationsModal
          isOpen={showAffectedReservationsModal}
          onClose={handleTripDeleteContinue}
          onConfirm={handleTripDeleteNotifyConfirm}
          affectedReservations={affectedReservations}
          tripId={pendingDeleteTripId || 0}
          contextText="Fshirja e orarit do të ndikojë në rezervimet e mëposhtme. Zgjidhni cilat të njoftohen:"
        />
      )}
    </div>
  );
};

export default Trips;
